// Gráfico de signos vitales del registro transanestésico (018A) y de
// monitorización/reevaluación post-anestésica (019) — Fase 1B, ver PLAN.md.
// Comparten el mismo eje temporal (0 a `MINUTO_MAX_SIGNOS_VITALES` minutos) y el mismo conjunto de
// parámetros continuos, así que el tipo de datos y el dibujo viven en un
// solo lugar, importado por ambos formularios (mismo criterio que
// `DatosOdontogramaPdf`/`DatosDenverPdf`: un diagrama, definido una vez).
//
// A diferencia del Test de Denver II (que deliberadamente dibuja un RESUMEN
// de texto en el PDF, no la cuadrícula completa — ver denverII.ts), acá el
// gráfico SÍ es parte reconocible del formulario oficial impreso: se dibuja
// el gráfico vectorial real (polilíneas + eventos), no un sustituto.
//
// **Limitación conocida, documentada a propósito**: los 7 parámetros
// comparten un único eje Y (igual que en pantalla, `GraficoSerieTemporal`),
// aunque sus rangos clínicos normales son muy distintos (SatO2 ~70-100%,
// PVC ~0-20cmH2O, TAS ~60-200mmHg, temperatura ~35-40°C) — mismo criterio
// ya aceptado en el proyecto para la curva térmica de 020 (temperatura/FC/FR
// en un solo eje). Con 7 series en vez de 3 el efecto es más marcado (PVC y
// temperatura quedan visualmente "aplanados" contra abajo). No se resuelve
// en esta tanda (normalizar por rango clínico por parámetro sería un cambio
// de diseño más grande, a evaluar si un anestesiólogo lo pide en revisión).
import { rgb, type PDFFont, type PDFPage } from 'pdf-lib';
import { BORDE_SUTIL, GRIS_OSCURO, INK, MUTED } from './layout';

export type ParametroSignosVitalesAnestesia = 'tas' | 'tad' | 'tam' | 'fc' | 'temperatura' | 'pvc' | 'sato2';

export interface PuntoTimelinePdf {
  readonly parametro: ParametroSignosVitalesAnestesia;
  readonly minuto: number;
  readonly valor: number;
}

export type TipoEventoAnestesia = 'inicio-anestesia' | 'induccion' | 'inicio-cirugia' | 'fin-cirugia' | 'fin-anestesia';

export interface EventoTimelinePdf {
  readonly tipo: TipoEventoAnestesia;
  readonly minuto: number;
}

/** Wire format serializable de `EspecificacionPdf` (modo 'msp', campo `diagramaSignosVitales`) — compartido por 018A y 019. */
export interface DatosSignosVitalesPdf {
  readonly puntos: readonly PuntoTimelinePdf[];
  readonly eventos: readonly EventoTimelinePdf[];
}

interface DefinicionParametroTimeline {
  readonly clave: ParametroSignosVitalesAnestesia;
  readonly etiqueta: string;
  readonly unidad: string;
  readonly color: ReturnType<typeof rgb>;
  readonly dashArray?: number[];
}

// Colores basados en la paleta hce-* (pine/pine-deep/clay/sage/muted, ver
// packages/hce-chart/src/styles.css) — 2 parámetros reusan color con
// `dashArray` para diferenciarse (mismo criterio de "color + trazo" que
// `PALETA_SERIES` en pantalla), sin agregar colores nuevos al sistema.
const PINE = rgb(0x1f / 255, 0x4d / 255, 0x47 / 255);
const PINE_DEEP = rgb(0x15 / 255, 0x37 / 255, 0x33 / 255);
const CLAY = rgb(0xb5 / 255, 0x56 / 255, 0x3c / 255);
const SAGE = rgb(0x6f / 255, 0x8f / 255, 0x6a / 255);

export const CATALOGO_PARAMETROS_TIMELINE: readonly DefinicionParametroTimeline[] = [
  { clave: 'tas', etiqueta: 'TAS', unidad: 'mmHg', color: PINE },
  { clave: 'tad', etiqueta: 'TAD', unidad: 'mmHg', color: CLAY },
  { clave: 'tam', etiqueta: 'TAM', unidad: 'mmHg', color: SAGE },
  { clave: 'fc', etiqueta: 'FC', unidad: 'lpm', color: PINE_DEEP },
  { clave: 'temperatura', etiqueta: 'Temperatura', unidad: '°C', color: MUTED },
  { clave: 'pvc', etiqueta: 'PVC', unidad: 'cmH2O', color: CLAY, dashArray: [4, 2] },
  { clave: 'sato2', etiqueta: 'SatO2', unidad: '%', color: SAGE, dashArray: [4, 2] },
];

const ETIQUETAS_EVENTO: Readonly<Record<TipoEventoAnestesia, string>> = {
  'inicio-anestesia': 'Inicio anestesia',
  induccion: 'Inducción',
  'inicio-cirugia': 'Inicio cirugía',
  'fin-cirugia': 'Fin cirugía',
  'fin-anestesia': 'Fin anestesia',
};

/**
 * Tope del eje temporal: el papel de 018A trae 9 bloques horarios (540 min) y el de 019 trae 6 (360 min). El eje se ajusta al dato
 * (`horizonteMinutos`): nunca baja de `MINUTO_MINIMO_EJE` (así los registros viejos, de 0-240, se dibujan igual que antes) y sube por
 * horas completas hasta este tope.
 */
export const MINUTO_MAX_SIGNOS_VITALES = 540;
export const MINUTO_MINIMO_EJE = 240;
const MINUTOS_POR_HORA = 60;
const ALTO_GRAFICO = 130;
const ALTO_TITULO = 16;
const ALTO_LEYENDA = 14;
const ALTO_EJE_X = 14;

/** Espacio vertical a reservar con `cursor.dibujo(altoDibujoSignosVitales(datos), ...)` — 0 si no hay nada que dibujar (el caller no debe reservar espacio vacío). */
export function altoDibujoSignosVitales(datos: DatosSignosVitalesPdf): number {
  if (datos.puntos.length === 0 && datos.eventos.length === 0) return 0;
  return ALTO_TITULO + ALTO_GRAFICO + ALTO_EJE_X + ALTO_LEYENDA;
}

/** Largo del eje X en minutos: hasta el último dato, redondeado a la hora, entre `MINUTO_MINIMO_EJE` y `MINUTO_MAX_SIGNOS_VITALES`. */
export function horizonteMinutos(datos: DatosSignosVitalesPdf): number {
  const ultimo = Math.max(0, ...datos.puntos.map((p) => p.minuto), ...datos.eventos.map((e) => e.minuto));
  const enHoras = Math.ceil(Math.min(ultimo, MINUTO_MAX_SIGNOS_VITALES) / MINUTOS_POR_HORA) * MINUTOS_POR_HORA;
  return Math.min(MINUTO_MAX_SIGNOS_VITALES, Math.max(MINUTO_MINIMO_EJE, enHoras));
}

function valorMinMax(puntos: readonly PuntoTimelinePdf[]): { min: number; max: number } {
  if (puntos.length === 0) return { min: 0, max: 1 };
  const valores = puntos.map((p) => p.valor);
  const min = Math.min(...valores);
  const max = Math.max(...valores);
  return min === max ? { min: min - 1, max: max + 1 } : { min, max };
}

/** Dibuja el gráfico vectorial sobre una `PDFPage` cruda, entregada por `CursorPdf.dibujo()`. Pura: no toca I/O, solo `pdf-lib`. */
export function dibujarSignosVitales(pagina: PDFPage, origen: { x: number; yTop: number }, anchoDisponible: number, fontRegular: PDFFont, datos: DatosSignosVitalesPdf): void {
  if (datos.puntos.length === 0 && datos.eventos.length === 0) return;

  let y = origen.yTop - 12;
  pagina.drawText('Registro transanestésico — signos vitales', { x: origen.x, y, size: 9, font: fontRegular, color: GRIS_OSCURO });
  y -= ALTO_TITULO;

  const areaAlto = ALTO_GRAFICO;
  const areaTop = y;
  const areaBottom = y - areaAlto;
  const { min, max } = valorMinMax(datos.puntos);

  const horizonte = horizonteMinutos(datos);
  const xDeMinuto = (minuto: number): number => origen.x + (Math.min(Math.max(minuto, 0), horizonte) / horizonte) * anchoDisponible;
  const yDeValor = (valor: number): number => areaBottom + ((valor - min) / (max - min)) * areaAlto;

  // Marco del área de dibujo.
  pagina.drawRectangle({ x: origen.x, y: areaBottom, width: anchoDisponible, height: areaAlto, borderColor: BORDE_SUTIL, borderWidth: 0.5, color: undefined });

  // Rejilla horaria (el papel marca bloques de una hora con cuartos de hora).
  for (let m = MINUTOS_POR_HORA; m < horizonte; m += MINUTOS_POR_HORA) {
    const x = xDeMinuto(m);
    pagina.drawLine({ start: { x, y: areaBottom }, end: { x, y: areaTop }, thickness: 0.3, color: BORDE_SUTIL });
  }

  // Eventos puntuales — línea vertical punteada + etiqueta rotada sería más legible, pero a este tamaño una etiqueta horizontal corta alcanza.
  for (const evento of datos.eventos) {
    const x = xDeMinuto(evento.minuto);
    pagina.drawLine({ start: { x, y: areaBottom }, end: { x, y: areaTop }, thickness: 0.5, color: MUTED, dashArray: [2, 2] });
  }

  // Polilíneas por parámetro — sin conectar entre puntos no consecutivos en el tiempo del mismo parámetro (mismo criterio que `connectNulls={false}` en pantalla).
  for (const def of CATALOGO_PARAMETROS_TIMELINE) {
    const puntos = datos.puntos.filter((p) => p.parametro === def.clave).sort((a, b) => a.minuto - b.minuto);
    for (let i = 0; i < puntos.length; i++) {
      const p = puntos[i];
      const cx = xDeMinuto(p.minuto);
      const cy = yDeValor(p.valor);
      pagina.drawCircle({ x: cx, y: cy, size: 1.5, color: def.color });
      if (i > 0) {
        const anterior = puntos[i - 1];
        pagina.drawLine({
          start: { x: xDeMinuto(anterior.minuto), y: yDeValor(anterior.valor) },
          end: { x: cx, y: cy },
          thickness: 1,
          color: def.color,
          dashArray: def.dashArray,
        });
      }
    }
  }

  y = areaBottom - ALTO_EJE_X + 10;
  for (let m = 0; m <= horizonte; m += MINUTOS_POR_HORA) {
    const texto = `${m}min`;
    const ancho = fontRegular.widthOfTextAtSize(texto, 7);
    const x = m === horizonte ? origen.x + anchoDisponible - ancho : m === 0 ? origen.x : xDeMinuto(m) - ancho / 2;
    pagina.drawText(texto, { x, y, size: 7, font: fontRegular, color: MUTED });
  }
  y -= ALTO_EJE_X - 4;

  // Leyenda: solo los parámetros con al menos 1 punto cargado + los eventos marcados.
  const parametrosConDato = CATALOGO_PARAMETROS_TIMELINE.filter((def) => datos.puntos.some((p) => p.parametro === def.clave));
  let cx = origen.x;
  for (const def of parametrosConDato) {
    pagina.drawRectangle({ x: cx, y: y - 6, width: 8, height: 8, color: def.color });
    const texto = `${def.etiqueta} (${def.unidad})`;
    pagina.drawText(texto, { x: cx + 11, y: y - 5, size: 7, font: fontRegular, color: INK });
    cx += 11 + fontRegular.widthOfTextAtSize(texto, 7) + 10;
  }
  if (datos.eventos.length > 0) {
    const etiquetasEventos = [...new Set(datos.eventos.map((e) => ETIQUETAS_EVENTO[e.tipo]))].join(', ');
    pagina.drawText(`Eventos: ${etiquetasEventos}`, { x: origen.x, y: y - ALTO_LEYENDA - 2, size: 7, font: fontRegular, color: MUTED });
  }
}
